package dev.lingvo.site.components.studio.editor

import dev.lingvo.shared.screenshots.doc.TypeRole
import dev.lingvo.shared.screenshots.text.FontResolver
import dev.lingvo.shared.screenshots.text.FontStack
import dev.lingvo.shared.screenshots.text.Script

/** One current font request; an old language/document completion cannot publish readiness. */
internal class EditorFontReadiness<K> {
    private var serial = 0
    private var current: K? = null
    private var ready: K? = null
    fun isReady(key: K): Boolean = ready == key
    fun begin(key: K): Int {
        current = key
        ready = null
        return ++serial
    }
    fun complete(token: Int, key: K, loaded: Boolean): Boolean {
        if (token != serial || current != key) return false
        ready = key.takeIf { loaded }
        return loaded
    }
}

/** Editor-only system faces avoid the CSS font block period of the requested web fonts. */
internal fun editorFallbackFont(script: Script, role: TypeRole = TypeRole.TEXT): FontStack {
    val regional = when (script) {
        Script.JAPANESE -> listOf("Hiragino Sans", "Yu Gothic", "Meiryo", "Noto Sans CJK JP")
        Script.KOREAN -> listOf("Apple SD Gothic Neo", "Malgun Gothic", "Noto Sans CJK KR")
        Script.HANS -> listOf("PingFang SC", "Microsoft YaHei", "Noto Sans CJK SC")
        Script.HANT, Script.HK -> listOf("PingFang TC", "Microsoft JhengHei", "Noto Sans CJK TC")
        Script.ARABIC, Script.URDU -> listOf("Geeza Pro", "Tahoma")
        Script.DEVANAGARI -> listOf("Kohinoor Devanagari", "Mangal")
        Script.THAI -> listOf("Thonburi", "Leelawadee UI")
        else -> emptyList()
    }
    return FontStack(regional + listOf("Arial", "DejaVu Sans", "Apple Color Emoji", "Segoe UI Emoji"),
        when (role) { TypeRole.SERIF -> "serif"; TypeRole.MONO -> "monospace"; else -> "sans-serif" })
}

internal val EditorFallbackFonts = FontResolver { role, _, _, _, script, _ -> editorFallbackFont(script, role) }
